import { useState, useEffect, useRef, useCallback } from 'react';
import { useAuth } from '@clerk/clerk-react';
import { api } from '../utils/api';

// Selected tags: { id, name }[] so we can show chips and submit tagIds
const TAG_SEARCH_DEBOUNCE_MS = 250;
const MIN_QUERY_LENGTH = 1;

export default function ItemForm({ item, onSuccess, onCancel }) {
  const { getToken } = useAuth();
  const isEditMode = !!item;
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [selectedTags, setSelectedTags] = useState([]); // { id, name }[]
  const [tagQuery, setTagQuery] = useState('');
  const [tagSuggestions, setTagSuggestions] = useState([]);
  const [tagSearching, setTagSearching] = useState(false);
  const [createTagPrompt, setCreateTagPrompt] = useState(null); // { name } when we can create
  const [creatingTag, setCreatingTag] = useState(false);
  const tagSearchAbortRef = useRef(null);
  const tagInputRef = useRef(null);
  const formTopRef = useRef(null);
  const [images, setImages] = useState([]); // Array of { file, preview, url }
  const [form, setForm] = useState({
    title: '',
    description: '',
    price: '',
    stock: '5',
  });
  const [categories, setCategories] = useState([]); // { id, name }[]
  const [categoryId, setCategoryId] = useState('');
  const [newCategoryName, setNewCategoryName] = useState('');
  const [creatingCategory, setCreatingCategory] = useState(false);
  const [showNewCategory, setShowNewCategory] = useState(false);

  const loadCategories = useCallback(async () => {
    const res = await api('/api/categories/mine', { getToken });
    if (!res.ok) return [];
    const list = await res.json();
    setCategories(list);
    return list;
  }, [getToken]);

  useEffect(() => {
    loadCategories();
  }, [loadCategories]);

  // Resolve tag IDs to names (for edit mode we only have ids initially)
  const ensureSelectedTagNames = useCallback(async (tagIds) => {
    if (!tagIds || tagIds.length === 0) return [];
    const res = await api('/api/tags');
    if (!res.ok) return tagIds.map(id => ({ id, name: `Tag #${id}` }));
    const all = await res.json();
    return tagIds.map(id => {
      const t = all.find(x => x.id === id);
      return { id, name: t ? t.name : `Tag #${id}` };
    });
  }, []);

  // Initialize form and images from item if editing
  useEffect(() => {
    if (item) {
      setForm({
        title: item.title || '',
        description: item.description || '',
        price: item.price?.toString() || '',
        stock: item.stockCount?.toString() || '5',
      });
      if (item.imageUrls && item.imageUrls.length > 0) {
        setImages(item.imageUrls.map(url => ({
          file: null,
          preview: url,
          url: url,
        })));
      }
      if (item.tagIds && item.tagIds.length > 0) {
        ensureSelectedTagNames(item.tagIds).then(setSelectedTags);
      }
      if (item.categoryId) {
        setCategoryId(String(item.categoryId));
      }
    }
  }, [item, ensureSelectedTagNames]);

  const createCategoryOnSpot = async () => {
    const name = newCategoryName.trim();
    if (!name || creatingCategory) return;
    setCreatingCategory(true);
    setError(null);
    try {
      const res = await api('/api/categories', {
        method: 'POST',
        getToken,
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ name }),
      });
      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        throw new Error(data.error || 'Failed to create category');
      }
      const cat = await res.json();
      await loadCategories();
      setCategoryId(String(cat.id));
      setNewCategoryName('');
      setShowNewCategory(false);
    } catch (err) {
      setError(err.message);
    } finally {
      setCreatingCategory(false);
    }
  };

  // When an error is shown (validation or API), scroll to top so user sees the message
  useEffect(() => {
    if (error) {
      formTopRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  }, [error]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm(prev => ({ ...prev, [name]: value }));
  };

  const handleImageChange = (e) => {
    const files = Array.from(e.target.files).slice(0, 3 - images.length);
    files.forEach(file => {
      if (file.type.startsWith('image/')) {
        const reader = new FileReader();
        reader.onloadend = () => {
          setImages(prev => [...prev, {
            file,
            preview: reader.result,
            url: null, // Will be set after upload
          }]);
        };
        reader.readAsDataURL(file);
      }
    });
  };

  const removeImage = (index) => {
    setImages(prev => prev.filter((_, i) => i !== index));
  };

  const addTag = (tag) => {
    setSelectedTags(prev => prev.some(t => t.id === tag.id) ? prev : [...prev, tag]);
    setTagQuery('');
    setTagSuggestions([]);
    setCreateTagPrompt(null);
    tagInputRef.current?.focus();
  };

  const removeTag = (tagId) => {
    setSelectedTags(prev => prev.filter(t => t.id !== tagId));
  };

  // Debounced tag search
  useEffect(() => {
    const q = tagQuery.trim().toLowerCase();
    if (q.length < MIN_QUERY_LENGTH) {
      setTagSuggestions([]);
      setCreateTagPrompt(null);
      return;
    }
    const timer = setTimeout(async () => {
      if (tagSearchAbortRef.current) tagSearchAbortRef.current.abort();
      tagSearchAbortRef.current = new AbortController();
      setTagSearching(true);
      setTagSuggestions([]);
      setCreateTagPrompt(null);
      try {
        const res = await api(`/api/tags/search?q=${encodeURIComponent(tagQuery.trim())}`, {
          signal: tagSearchAbortRef.current.signal,
        });
        if (!res.ok) return;
        const data = await res.json();
        const alreadySelected = selectedTags.map(t => t.id);
        const filtered = (data || []).filter(t => !alreadySelected.includes(t.id));
        setTagSuggestions(filtered);
        if (filtered.length === 0 && q.length >= MIN_QUERY_LENGTH) {
          const exact = (data || []).some(t => t.name.toLowerCase() === q);
          if (!exact) setCreateTagPrompt({ name: tagQuery.trim() });
        }
      } catch (err) {
        if (err.name !== 'AbortError') setTagSuggestions([]);
      } finally {
        setTagSearching(false);
      }
    }, TAG_SEARCH_DEBOUNCE_MS);
    return () => {
      clearTimeout(timer);
      if (tagSearchAbortRef.current) tagSearchAbortRef.current.abort();
    };
  }, [tagQuery, selectedTags]);

  const createTag = async () => {
    if (!createTagPrompt?.name || creatingTag) return;
    setCreatingTag(true);
    setError(null);
    try {
      const res = await api('/api/tags', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ name: createTagPrompt.name }),
      });
      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        throw new Error(data.error || 'Failed to create tag');
      }
      const tag = await res.json();
      addTag(tag);
      setCreateTagPrompt(null);
    } catch (err) {
      setError(err.message);
    } finally {
      setCreatingTag(false);
    }
  };

  const uploadImage = async (file) => {
    const formData = new FormData();
    formData.append('file', file);

    const res = await api('/api/images/upload', {
      getToken,
      method: 'POST',
      body: formData,
    });

    if (!res.ok) {
      const data = await res.json().catch(() => ({}));
      throw new Error(data.error || data.details || `Failed to upload image (${res.status})`);
    }

    const data = await res.json();
    let url = data.url || data.imageUrl;
    if (!url) throw new Error('Upload returned no URL');
    try {
      const parsed = new URL(url, window.location.origin);
      if (parsed.pathname.startsWith('/public/') || parsed.pathname.startsWith('/uploads/')) {
        url = parsed.pathname;
      }
    } catch {
      // keep original
    }
    return url;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);
    setLoading(true);

    try {
      // Validate form
      if (!form.title.trim()) {
        throw new Error('Title is required');
      }
      if (!form.price || parseFloat(form.price) <= 0) {
        throw new Error('Price must be greater than 0');
      }
      const stock = parseInt(form.stock, 10);
      if (isNaN(stock) || stock < 0) {
        throw new Error('Stock count must be a non-negative number');
      }
      if (images.length === 0) {
        throw new Error('At least one image is required');
      }
      if (selectedTags.length === 0) {
        throw new Error('At least one tag is required');
      }
      if (!categoryId) {
        throw new Error('Category is required');
      }

      const tagIds = selectedTags.map(t => t.id);
      const payload = {
        title: form.title.trim(),
        description: form.description.trim() || null,
        price: parseFloat(form.price),
        stockCount: stock,
        tagIds,
        categoryId: parseInt(categoryId, 10),
      };

      if (isEditMode) {
        // Edit: upload any new images, then PUT item with all data
        const imageUrls = [];
        for (const img of images) {
          if (img.url) imageUrls.push(img.url);
          else imageUrls.push(await uploadImage(img.file));
        }
        const res = await api(`/api/items/${item.id}`, {
          method: 'PUT',
          getToken,
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ ...payload, imageUrls }),
        });
        if (!res.ok) {
          const data = await res.json().catch(() => ({}));
          throw new Error(data.error || `Error ${res.status}`);
        }
        if (onSuccess) onSuccess();
        return;
      }

      // Create: upload images first so listing shows photos immediately
      const imageUrls = [];
      for (const img of images) {
        if (img.url) imageUrls.push(img.url);
        else if (img.file) imageUrls.push(await uploadImage(img.file));
      }
      const createRes = await api('/api/items/create', {
        method: 'POST',
        getToken,
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ ...payload, imageUrls }),
      });
      if (!createRes.ok) {
        const data = await createRes.json().catch(() => ({}));
        throw new Error(data.error || `Error ${createRes.status}`);
      }
      const created = await createRes.json();
      if (onSuccess) onSuccess(created);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div ref={formTopRef} className="flex flex-col gap-6">
      <h1 className="text-3xl font-semibold">{isEditMode ? 'Edit Item' : 'Create Item'}</h1>

      {error && (
        <p className="rounded-xl bg-red-100 dark:bg-red-900/30 text-red-800 dark:text-red-200 p-4 text-lg">
          {error}
        </p>
      )}

      <form onSubmit={handleSubmit} className="flex flex-col gap-6">
        {/* Title */}
        <label className="flex flex-col gap-2">
          <span className="text-lg font-medium">Title *</span>
          <input
            type="text"
            name="title"
            value={form.title}
            onChange={handleChange}
            className="h-16 text-2xl w-full rounded-xl border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-800 px-4"
            placeholder="Item title"
            required
            disabled={loading}
          />
        </label>

        {/* Description */}
        <label className="flex flex-col gap-2">
          <span className="text-lg font-medium">Description</span>
          <textarea
            name="description"
            value={form.description}
            onChange={handleChange}
            rows={6}
            className="w-full rounded-xl border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-800 px-4 py-4 text-lg resize-y"
            placeholder="Item description"
            disabled={loading}
          />
        </label>

        {/* Price */}
        <label className="flex flex-col gap-2">
          <span className="text-lg font-medium">Price ($) *</span>
          <input
            type="number"
            name="price"
            value={form.price}
            onChange={handleChange}
            step="0.01"
            min="0.01"
            className="h-16 text-2xl w-full rounded-xl border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-800 px-4"
            placeholder="0.00"
            required
            disabled={loading}
          />
        </label>

        {/* Stock */}
        <label className="flex flex-col gap-2">
          <span className="text-lg font-medium">Stock Count *</span>
          <input
            type="number"
            name="stock"
            value={form.stock}
            onChange={handleChange}
            min="0"
            className="h-16 text-2xl w-full rounded-xl border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-800 px-4"
            placeholder="5"
            required
            disabled={loading}
          />
        </label>

        {/* Image Upload */}
        <div className="flex flex-col gap-2">
          <span className="text-lg font-medium">Images (up to 3) *</span>
          {images.length < 3 && (
            <label className="cursor-pointer">
              <input
                type="file"
                accept="image/*"
                onChange={handleImageChange}
                className="hidden"
                disabled={loading}
                multiple={images.length === 0}
              />
              <div className="h-32 w-full rounded-xl border-2 border-dashed border-slate-300 dark:border-slate-600 bg-slate-50 dark:bg-slate-800/50 flex items-center justify-center text-lg text-slate-600 dark:text-slate-400 hover:border-indigo-500 transition-colors">
                + Add Image {images.length > 0 && `(${images.length}/3)`}
              </div>
            </label>
          )}

          {/* Image Carousel Preview */}
          {images.length > 0 && (
            <div className="flex gap-4 overflow-x-auto pb-2 snap-x snap-mandatory">
              {images.map((img, index) => (
                <div key={index} className="flex-shrink-0 relative snap-start">
                  <div className="w-40 h-40 rounded-xl overflow-hidden border-2 border-slate-300 dark:border-slate-600 shadow-md">
                    <img
                      src={img.preview}
                      alt={`Preview ${index + 1}`}
                      className="w-full h-full object-contain"
                    />
                  </div>
                  <button
                    type="button"
                    onClick={() => removeImage(index)}
                    className="absolute -top-2 -right-2 w-10 h-10 rounded-full bg-red-500 text-white flex items-center justify-center text-xl font-bold hover:bg-red-600 active:bg-red-700 transition-colors shadow-lg"
                    disabled={loading}
                    aria-label={`Remove image ${index + 1}`}
                  >
                    ×
                  </button>
                  <div className="absolute bottom-0 left-0 right-0 bg-black/50 text-white text-xs text-center py-1">
                    {index + 1} / {images.length}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Category (required) */}
        <div className="flex flex-col gap-2">
          <span className="text-lg font-medium">Category *</span>
          <p className="text-sm text-slate-600 dark:text-slate-400">
            Organizes this listing on your storefront. Tags still help with search.
          </p>
          {categories.length === 0 ? (
            <p className="text-sm text-amber-700 dark:text-amber-300">
              No categories yet — create one below, or set them up from My Items first.
            </p>
          ) : (
            <select
              value={categoryId}
              onChange={(e) => setCategoryId(e.target.value)}
              disabled={loading}
              className="h-12 w-full rounded-xl border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-800 px-4 text-lg"
              required
            >
              <option value="">Select a category…</option>
              {categories.map((c) => (
                <option key={c.id} value={c.id}>{c.name}</option>
              ))}
            </select>
          )}
          {!showNewCategory ? (
            <button
              type="button"
              onClick={() => setShowNewCategory(true)}
              className="self-start text-sm font-medium text-emerald-700 dark:text-emerald-400 hover:underline"
            >
              + New category
            </button>
          ) : (
            <div className="flex flex-col sm:flex-row gap-2">
              <input
                type="text"
                value={newCategoryName}
                onChange={(e) => setNewCategoryName(e.target.value)}
                placeholder="e.g. Jewelry, Tools…"
                className="h-12 flex-1 rounded-xl border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-800 px-4 text-base"
                disabled={creatingCategory || loading}
              />
              <button
                type="button"
                onClick={createCategoryOnSpot}
                disabled={creatingCategory || !newCategoryName.trim()}
                className="h-12 px-4 rounded-xl bg-emerald-600 text-white font-semibold hover:bg-emerald-700 disabled:opacity-50"
              >
                {creatingCategory ? 'Creating…' : 'Create'}
              </button>
              <button
                type="button"
                onClick={() => { setShowNewCategory(false); setNewCategoryName(''); }}
                className="h-12 px-4 rounded-xl border border-slate-300 dark:border-slate-600 font-medium"
              >
                Cancel
              </button>
            </div>
          )}
        </div>

        {/* Tags: autocomplete + selected chips + create new */}
        <div className="flex flex-col gap-2">
          <span className="text-lg font-medium">Tags (at least one) *</span>
          <p className="text-sm text-slate-600 dark:text-slate-400">Type to search; pick a suggestion or create a new tag.</p>
          <div className="relative">
            <input
              ref={tagInputRef}
              type="text"
              value={tagQuery}
              onChange={(e) => setTagQuery(e.target.value)}
              onBlur={() => setTimeout(() => { setTagSuggestions([]); setCreateTagPrompt(null); }, 200)}
              placeholder="Search or add a tag..."
              className="h-12 w-full rounded-xl border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-800 px-4 text-lg"
              disabled={loading}
              autoComplete="off"
            />
            {tagSearching && (
              <div className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400">
                <span className="animate-pulse text-sm">Searching…</span>
              </div>
            )}
            {(tagSuggestions.length > 0 || createTagPrompt) && (
              <div className="absolute z-20 mt-1 w-full rounded-xl border border-slate-200 dark:border-slate-600 bg-white dark:bg-slate-800 shadow-lg max-h-56 overflow-y-auto">
                {tagSuggestions.map((tag) => (
                  <button
                    key={tag.id}
                    type="button"
                    onClick={() => addTag(tag)}
                    className="w-full px-4 py-3 text-left text-base hover:bg-slate-100 dark:hover:bg-slate-700 first:rounded-t-xl last:rounded-b-xl"
                  >
                    {tag.name}
                  </button>
                ))}
                {createTagPrompt && (
                  <div className="border-t border-slate-200 dark:border-slate-600 px-4 py-3 bg-indigo-50 dark:bg-indigo-900/20">
                    <p className="text-sm text-slate-600 dark:text-slate-400 mb-2">No tag named “{createTagPrompt.name}”. Create it?</p>
                    <button
                      type="button"
                      onClick={createTag}
                      disabled={creatingTag}
                      className="px-4 py-2 rounded-lg bg-indigo-600 text-white font-medium hover:bg-indigo-700 disabled:opacity-50"
                    >
                      {creatingTag ? 'Creating…' : `Create tag “${createTagPrompt.name}”`}
                    </button>
                  </div>
                )}
              </div>
            )}
          </div>
          {selectedTags.length > 0 && (
            <div className="flex flex-wrap gap-2 mt-1">
              {selectedTags.map((t) => (
                <span
                  key={t.id}
                  className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-indigo-600 text-white text-sm font-medium"
                >
                  {t.name}
                  <button
                    type="button"
                    onClick={() => removeTag(t.id)}
                    disabled={loading}
                    className="w-5 h-5 rounded-full hover:bg-indigo-500 flex items-center justify-center text-lg leading-none"
                    aria-label={`Remove ${t.name}`}
                  >
                    ×
                  </button>
                </span>
              ))}
            </div>
          )}
        </div>

        {/* Submit Buttons */}
        <div className="flex flex-col gap-4">
          <button
            type="submit"
            disabled={loading}
            className="h-16 text-2xl w-full rounded-xl font-semibold bg-indigo-600 text-white hover:bg-indigo-700 active:bg-indigo-800 disabled:opacity-50 transition-colors"
          >
            {loading ? (isEditMode ? 'Updating…' : 'Creating…') : (isEditMode ? 'Update Item' : 'Create Item')}
          </button>
          {onCancel && (
            <button
              type="button"
              onClick={onCancel}
              disabled={loading}
              className="h-16 text-xl w-full rounded-xl font-medium bg-slate-200 dark:bg-slate-700 text-slate-800 dark:text-slate-200 hover:bg-slate-300 dark:hover:bg-slate-600 disabled:opacity-50 transition-colors"
            >
              Cancel
            </button>
          )}
        </div>
      </form>
    </div>
  );
}
